import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { MapPin, Calendar, CheckCircle, Award, Wrench } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";

const SuccessStoriesSection = () => {
  const { t } = useTranslation();
  return (
    <section id="casos-exito" className="py-20 bg-enausa-light">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-enausa-navy mb-6">
            {t('success.title')}
          </h2>
          <p className="text-xl text-enausa-gray max-w-3xl mx-auto">
            {t('success.subtitle')}
          </p>
        </div>

        {/* Zener Case Study */}
        <div className="max-w-6xl mx-auto">
          <Card className="shadow-strong hover:shadow-strong transition-all duration-300 bg-gradient-card">
            <CardHeader className="pb-6">
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                <div>
                  <CardTitle className="text-2xl md:text-3xl text-enausa-navy mb-2">
                    {t('success.projectTitle')}
                  </CardTitle>
                  <CardDescription className="text-lg text-enausa-gray">
                    {t('success.projectSubtitle')}
                  </CardDescription>
                </div>
                <div className="flex items-center text-enausa-navy">
                  <Award className="w-6 h-6 mr-2" />
                  <span className="text-sm font-semibold">{t('success.internationalBadge')}</span>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-8">
              {/* Project Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-start space-x-3">
                  <MapPin className="w-6 h-6 text-enausa-navy mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-enausa-navy mb-1">{t('success.locationsTitle')}</h4>
                    <p className="text-enausa-gray text-sm">{t('success.locations1')}</p>
                    <p className="text-enausa-gray text-sm">{t('success.locations2')}</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <Calendar className="w-6 h-6 text-enausa-navy mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-enausa-navy mb-1">{t('success.clientTitle')}</h4>
                    <p className="text-enausa-gray text-sm">{t('success.clientName')}</p>
                    <p className="text-enausa-gray text-sm">{t('success.clientDesc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <Wrench className="w-6 h-6 text-enausa-navy mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-enausa-navy mb-1">{t('success.servicesTitle')}</h4>
                    <p className="text-enausa-gray text-sm">{t('success.service1')}</p>
                    <p className="text-enausa-gray text-sm">{t('success.service2')}</p>
                  </div>
                </div>
              </div>

              {/* Project Description */}
              <div className="bg-white p-6 rounded-lg border-l-4 border-enausa-navy">
                <h4 className="text-xl font-semibold text-enausa-navy mb-3">{t('success.descriptionTitle')}</h4>
                <p className="text-enausa-gray leading-relaxed mb-4">
                  {t('success.p1')}
                </p>
                <p className="text-enausa-gray leading-relaxed">
                  {t('success.p2')}
                </p>
              </div>

              {/* Results and Achievements */}
              <div>
                <h4 className="text-xl font-semibold text-enausa-navy mb-4">{t('success.resultsTitle')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    t('success.r1'),
                    t('success.r2'),
                    t('success.r3'),
                    t('success.r4'),
                    t('success.r5'),
                    t('success.r6')
                  ].map((achievement, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-enausa-gray text-sm">{achievement}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Impact Statement */}
              <div className="bg-gradient-to-r from-enausa-navy to-enausa-navy-light p-6 rounded-lg text-white">
                <h4 className="text-xl font-semibold mb-3">{t('success.impactTitle')}</h4>
                <p className="text-gray-100 leading-relaxed">
                  "{t('success.impactQuote')}"
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-12">
          <p className="text-lg text-enausa-gray mb-4">
            {t('contact.subtitle')}
          </p>
          <button
            onClick={() => document.getElementById('servicios')?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center px-8 py-3 bg-enausa-navy text-white font-semibold rounded-lg hover:bg-enausa-navy-light transition-colors duration-300"
          >
            {t('hero.cta')}
          </button>
        </div>
      </div>
    </section>
  );
};

export default SuccessStoriesSection;